import type { IsoDate } from '../../domain/dates';
import type { FederalState } from '../../domain/holidays';
import type { Household } from '../../domain/household';
import { MESSAGES, type Language } from '../i18n/messages';
import type { AppData, Appearance, StoredPlan, StoredTrip } from './app-data';

/**
 * DE: Alle Änderungen an den gespeicherten Daten.
 * EN: All changes to the stored data.
 */
export type Action =
  | { type: 'set-language'; language: Language }
  | { type: 'set-appearance'; appearance: Appearance }
  | { type: 'dismiss-install-hint' }
  | { type: 'set-federal-state'; federalState: FederalState | undefined }
  | { type: 'add-closed-date'; date: IsoDate }
  | { type: 'remove-closed-date'; date: IsoDate }
  | { type: 'add-low-energy-date'; date: IsoDate }
  | { type: 'store-plan'; plan: StoredPlan }
  | { type: 'toggle-item'; tripId: string; key: string }
  | { type: 'mark-bought'; tripId: string }
  | { type: 'restore-backup'; data: AppData };

const withDate = (dates: readonly IsoDate[], date: IsoDate) =>
  [...new Set([...dates, date])].sort();

function withFederalState(household: Household, federalState: FederalState | undefined): Household {
  const copy = { ...household };
  delete copy.federalState;
  return federalState ? { ...copy, federalState } : copy;
}

function storePlan(plans: readonly StoredPlan[], plan: StoredPlan): StoredPlan[] {
  return [...plans.filter((other) => other.period.start !== plan.period.start), plan].sort((a, b) =>
    a.period.start.localeCompare(b.period.start),
  );
}

function updateTrip(data: AppData, tripId: string, change: (trip: StoredTrip) => StoredTrip) {
  const plans = data.plans.map((plan) => ({
    ...plan,
    trips: plan.trips.map((trip) => (trip.id === tripId ? change(trip) : trip)),
  }));
  return { ...data, plans };
}

const toggled = (keys: readonly string[], key: string) =>
  keys.includes(key) ? keys.filter((other) => other !== key) : [...keys, key];

function updateHousehold(data: AppData, change: (household: Household) => Household): AppData {
  return { ...data, household: change(data.household) };
}

// DE: Voreingestellte Namen wechseln mit der Sprache; selbst vergebene Namen bleiben.
// EN: Preset names change with the language; names given by the user stay.
function withMemberNames(household: Household, from: Language, to: Language): Household {
  const before = MESSAGES[from].defaultMemberNames;
  const after = MESSAGES[to].defaultMemberNames;
  const members = household.members.map((member, index) =>
    member.name === before[index] ? { ...member, name: after[index] ?? member.name } : member,
  );
  return { ...household, members };
}

/**
 * DE: Neue Daten nach einer Änderung; die alten Daten bleiben unverändert.
 * EN: New data after a change; the old data stays unchanged.
 */
export function reducer(data: AppData, action: Action): AppData {
  switch (action.type) {
    case 'set-language':
      return {
        ...updateHousehold(data, (h) => withMemberNames(h, data.language, action.language)),
        language: action.language,
      };
    case 'set-appearance':
      return { ...data, appearance: action.appearance };
    case 'dismiss-install-hint':
      return { ...data, installHintDismissed: true };
    case 'set-federal-state':
      return updateHousehold(data, (h) => withFederalState(h, action.federalState));
    case 'add-closed-date':
      return updateHousehold(data, (h) => ({
        ...h,
        closedDates: withDate(h.closedDates, action.date),
      }));
    case 'remove-closed-date':
      return updateHousehold(data, (h) => ({
        ...h,
        closedDates: h.closedDates.filter((date) => date !== action.date),
      }));
    case 'add-low-energy-date':
      return updateHousehold(data, (h) => ({
        ...h,
        lowEnergyDates: withDate(h.lowEnergyDates, action.date),
      }));
    case 'store-plan':
      return { ...data, plans: storePlan(data.plans, action.plan) };
    case 'toggle-item':
      return updateTrip(data, action.tripId, (t) => ({
        ...t,
        checked: toggled(t.checked, action.key),
      }));
    case 'mark-bought':
      return updateTrip(data, action.tripId, (trip) => ({ ...trip, bought: true }));
    case 'restore-backup':
      return action.data;
  }
}
